import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Form,
  Grid,
  Header,
  Input,
  Label,
  Message,
  Popup,
  Segment,
  Statistic,
  Table,
} from 'semantic-ui-react';
import {
  API,
  isAdmin,
  showError,
  showSuccess,
  timestamp2string,
} from '../../helpers';

const EMPTY_FORM = {
  id: 0,
  name: '',
  description: '',
  processing_types: [],
  enabled: true,
};

const normalizeCapability = (capability) => ({
  id: capability?.id || 0,
  name: capability?.name || '',
  description: capability?.description || '',
  processing_types: capability?.processing_types || [],
  enabled: capability?.enabled !== false,
  created_time: capability?.created_time || 0,
  updated_time: capability?.updated_time || 0,
});

const sortCapabilities = (capabilities) =>
  [...capabilities].sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
  );

const ModelCapability = () => {
  const { t } = useTranslation();
  const [capabilities, setCapabilities] = useState([]);
  const [processingTypeDefinitions, setProcessingTypeDefinitions] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  const processingTypeLabels = useMemo(() => {
    const labels = {};
    processingTypeDefinitions.forEach((definition) => {
      labels[definition.code] = t(
        `model_capability.types.${definition.code}`,
        { defaultValue: definition.name }
      );
    });
    return labels;
  }, [processingTypeDefinitions, t]);

  const processingTypeOptions = useMemo(
    () =>
      processingTypeDefinitions.map((definition) => ({
        key: definition.code,
        text: processingTypeLabels[definition.code] || definition.name,
        value: definition.code,
      })),
    [processingTypeDefinitions, processingTypeLabels]
  );

  const filteredCapabilities = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (keyword === '') {
      return capabilities;
    }
    return capabilities.filter((capability) => {
      if (capability.name.toLowerCase().includes(keyword)) {
        return true;
      }
      if ((capability.description || '').toLowerCase().includes(keyword)) {
        return true;
      }
      return (capability.processing_types || []).some((typeCode) =>
        (processingTypeLabels[typeCode] || typeCode).toLowerCase().includes(keyword)
      );
    });
  }, [capabilities, processingTypeLabels, searchKeyword]);

  const capabilityStats = useMemo(() => {
    const enabledCount = capabilities.filter((capability) => capability.enabled)
      .length;
    return {
      total: capabilities.length,
      enabled: enabledCount,
      disabled: capabilities.length - enabledCount,
    };
  }, [capabilities]);

  const loadPageData = async () => {
    setLoading(true);
    const [capabilityRes, typeRes] = await Promise.all([
      API.get('/api/model-capability/'),
      API.get('/api/model-capability/types'),
    ]);
    if (!capabilityRes || !typeRes) {
      setLoading(false);
      return;
    }

    const capabilityPayload = capabilityRes.data || {};
    const typePayload = typeRes.data || {};

    if (!capabilityPayload.success) {
      showError(capabilityPayload.message || t('model_capability.messages.load_failed'));
      setLoading(false);
      return;
    }
    if (!typePayload.success) {
      showError(typePayload.message || t('model_capability.messages.load_failed'));
      setLoading(false);
      return;
    }

    setCapabilities(
      sortCapabilities((capabilityPayload.data || []).map(normalizeCapability))
    );
    setProcessingTypeDefinitions(typePayload.data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (!isAdmin()) {
      return;
    }
    loadPageData().then();
  }, []);

  const resetForm = () => {
    setFormData(EMPTY_FORM);
  };

  const handleInputChange = (_, { name, value, checked }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked !== undefined ? checked : value,
    }));
  };

  const submitForm = async () => {
    if (formData.name.trim() === '') {
      showError(t('model_capability.messages.name_required'));
      return;
    }
    if (formData.processing_types.length === 0) {
      showError(t('model_capability.messages.processing_types_required'));
      return;
    }

    setSaving(true);
    const payload = {
      id: formData.id,
      name: formData.name.trim(),
      description: formData.description.trim(),
      processing_types: formData.processing_types,
      enabled: formData.enabled,
    };

    const res = formData.id
      ? await API.put('/api/model-capability/', payload)
      : await API.post('/api/model-capability/', payload);

    setSaving(false);
    if (!res) {
      return;
    }

    const { success, message, data } = res.data || {};
    if (!success) {
      showError(message || t('model_capability.messages.save_failed'));
      return;
    }

    const nextCapability = normalizeCapability(data);
    setCapabilities((prev) => {
      const next = prev.filter((item) => item.id !== nextCapability.id);
      next.push(nextCapability);
      return sortCapabilities(next);
    });
    resetForm();
    showSuccess(
      t(
        formData.id
          ? 'model_capability.messages.update_success'
          : 'model_capability.messages.create_success'
      )
    );
  };

  const startEdit = (capability) => {
    setFormData(normalizeCapability(capability));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const removeCapability = async (capability) => {
    const res = await API.delete(`/api/model-capability/${capability.id}`);
    if (!res) {
      return;
    }
    const { success, message } = res.data || {};
    if (!success) {
      showError(message || t('model_capability.messages.delete_failed'));
      return;
    }
    setCapabilities((prev) =>
      prev.filter((item) => item.id !== capability.id)
    );
    if (formData.id === capability.id) {
      resetForm();
    }
    showSuccess(t('model_capability.messages.delete_success'));
  };

  if (!isAdmin()) {
    return (
      <div className='dashboard-container'>
        <Card fluid className='chart-card'>
          <Card.Content>
            <Card.Header className='header'>
              {t('model_capability.title')}
            </Card.Header>
            <Message negative>
              {t('model_capability.no_access')}
            </Message>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {t('model_capability.title')}
          </Card.Header>

          <Segment
            style={{
              marginTop: '1rem',
              borderRadius: '14px',
              border: '1px solid rgba(34, 36, 38, 0.08)',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Grid stackable columns={2} verticalAlign='middle'>
              <Grid.Column>
                <Header as='h3' style={{ marginBottom: '0.5rem' }}>
                  {t('model_capability.intro_title')}
                </Header>
                <p
                  style={{
                    color: '#5b6472',
                    lineHeight: 1.7,
                    marginBottom: 0,
                  }}
                >
                  {t('model_capability.intro')}
                </p>
              </Grid.Column>
              <Grid.Column textAlign='right'>
                <Statistic.Group size='tiny'>
                  <Statistic>
                    <Statistic.Value>{capabilityStats.total}</Statistic.Value>
                    <Statistic.Label>
                      {t('model_capability.stats.total')}
                    </Statistic.Label>
                  </Statistic>
                  <Statistic color='green'>
                    <Statistic.Value>{capabilityStats.enabled}</Statistic.Value>
                    <Statistic.Label>
                      {t('model_capability.stats.enabled')}
                    </Statistic.Label>
                  </Statistic>
                  <Statistic color='grey'>
                    <Statistic.Value>{capabilityStats.disabled}</Statistic.Value>
                    <Statistic.Label>
                      {t('model_capability.stats.disabled')}
                    </Statistic.Label>
                  </Statistic>
                </Statistic.Group>
              </Grid.Column>
            </Grid>
          </Segment>

          <Message info>
            <Header as='h4'>{t('model_capability.client_api.title')}</Header>
            <p style={{ marginBottom: '0.5rem' }}>
              <code>/v1/models/capabilities</code>{' '}
              {t('model_capability.client_api.public')}
            </p>
            <p style={{ marginBottom: 0 }}>
              <code>/api/user/model_capabilities</code>{' '}
              {t('model_capability.client_api.dashboard')}
            </p>
          </Message>

          <Grid stackable columns={2}>
            <Grid.Column width={7}>
              <Segment
                style={{
                  borderRadius: '14px',
                  border: '1px solid rgba(34, 36, 38, 0.08)',
                  boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)',
                }}
              >
                <Header as='h4' style={{ marginTop: 0 }}>
                  {formData.id
                    ? t('model_capability.form.title_edit')
                    : t('model_capability.form.title_create')}
                </Header>
                <Form onSubmit={submitForm} loading={saving}>
                  <Form.Input
                    label={t('model_capability.form.name')}
                    placeholder={t('model_capability.form.name_placeholder')}
                    name='name'
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  <Form.Dropdown
                    label={t('model_capability.form.processing_types')}
                    placeholder={t(
                      'model_capability.form.processing_types_placeholder'
                    )}
                    name='processing_types'
                    fluid
                    multiple
                    selection
                    search
                    options={processingTypeOptions}
                    value={formData.processing_types}
                    onChange={handleInputChange}
                  />
                  <Form.TextArea
                    label={t('model_capability.form.description')}
                    placeholder={t(
                      'model_capability.form.description_placeholder'
                    )}
                    name='description'
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                  <Form.Checkbox
                    toggle
                    label={t('model_capability.form.enabled')}
                    name='enabled'
                    checked={formData.enabled}
                    onChange={handleInputChange}
                  />
                  <Button
                    primary
                    type='submit'
                    loading={saving}
                    disabled={loading}
                  >
                    {formData.id
                      ? t('model_capability.buttons.update')
                      : t('model_capability.buttons.create')}
                  </Button>
                  <Button
                    type='button'
                    onClick={resetForm}
                    disabled={saving}
                  >
                    {t('model_capability.buttons.reset')}
                  </Button>
                </Form>
              </Segment>
            </Grid.Column>

            <Grid.Column width={9}>
              <Segment
                style={{
                  borderRadius: '14px',
                  border: '1px solid rgba(34, 36, 38, 0.08)',
                  boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)',
                }}
              >
                <Form onSubmit={(event) => event.preventDefault()}>
                  <Input
                    icon='search'
                    fluid
                    iconPosition='left'
                    placeholder={t('model_capability.search')}
                    value={searchKeyword}
                    onChange={(event, { value }) => setSearchKeyword(value)}
                  />
                </Form>
                <Table
                  basic='very'
                  celled
                  striped
                  stackable
                  style={{ marginTop: '1rem' }}
                >
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {t('model_capability.table.model')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t('model_capability.table.processing_types')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t('model_capability.table.status')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t('model_capability.table.updated_time')}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {t('model_capability.table.actions')}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {filteredCapabilities.length === 0 ? (
                      <Table.Row>
                        <Table.Cell colSpan='5' textAlign='center'>
                          {t('model_capability.table.empty')}
                        </Table.Cell>
                      </Table.Row>
                    ) : (
                      filteredCapabilities.map((capability) => (
                        <Table.Row key={capability.id}>
                          <Table.Cell>
                            <Header as='h5' style={{ marginBottom: '0.35rem' }}>
                              {capability.name}
                            </Header>
                            <div
                              style={{
                                color: '#6b7280',
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {capability.description ||
                                t('model_capability.table.no_description')}
                            </div>
                          </Table.Cell>
                          <Table.Cell>
                            {(capability.processing_types || []).map((typeCode) => (
                              <Label
                                key={`${capability.id}-${typeCode}`}
                                basic
                                color='blue'
                                style={{ marginBottom: '0.35rem' }}
                              >
                                {processingTypeLabels[typeCode] || typeCode}
                              </Label>
                            ))}
                          </Table.Cell>
                          <Table.Cell>
                            <Label basic color={capability.enabled ? 'green' : 'grey'}>
                              {capability.enabled
                                ? t('model_capability.status.enabled')
                                : t('model_capability.status.disabled')}
                            </Label>
                          </Table.Cell>
                          <Table.Cell>
                            {capability.updated_time
                              ? timestamp2string(capability.updated_time)
                              : '-'}
                          </Table.Cell>
                          <Table.Cell>
                            <Button
                              size='tiny'
                              onClick={() => startEdit(capability)}
                            >
                              {t('model_capability.buttons.edit')}
                            </Button>
                            <Popup
                              trigger={
                                <Button size='tiny' negative>
                                  {t('model_capability.buttons.delete')}
                                </Button>
                              }
                              on='click'
                              flowing
                              hoverable
                            >
                              <Button
                                negative
                                size='tiny'
                                onClick={() => removeCapability(capability)}
                              >
                                {t('model_capability.buttons.confirm_delete')}
                              </Button>
                            </Popup>
                          </Table.Cell>
                        </Table.Row>
                      ))
                    )}
                  </Table.Body>
                </Table>
              </Segment>
            </Grid.Column>
          </Grid>
        </Card.Content>
      </Card>
    </div>
  );
};

export default ModelCapability;
